import { FC } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { EditableDataTable, EditableColumn, ActionButton, Dropdown, DropdownItem } from '@/components/ui';
import { useCanEdit } from '@/hooks/useEditMode';
import { Pitch } from '../types';

interface PitchesTabProps {
  pitches: Pitch[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

/**
 * Sales Pitches Tab Component
 * Create and manage sales pitches linked to targets
 */
export const PitchesTab: FC<PitchesTabProps> = ({
  pitches,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const canEdit = useCanEdit();

  const columns: EditableColumn<Pitch>[] = [
    {
      key: 'targetLabel',
      header: 'Target',
      accessor: 'targetLabel',
    },
    {
      key: 'text',
      header: 'Pitch',
      render: (pitch) => (
        <span className="text-gray-700">"{pitch.text}"</span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: 'Actions',
            className: 'w-16',
            render: (_: any, index: number) => {
              const items: DropdownItem[] = [
                {
                  key: 'edit',
                  label: 'Edit',
                  icon: Edit2,
                  onClick: () => onEdit(index),
                  variant: 'info',
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: Trash2,
                  onClick: () => onDelete(index),
                  variant: 'danger',
                },
              ];

              return <Dropdown items={items} />;
            },
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Pitches by Target
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Create and manage sales pitches linked to targets
          </p>
        </div>
        {canEdit && (
          <ActionButton variant="success" onClick={onAdd}>
            <Plus className="w-4 h-4" />
            New Pitch
          </ActionButton>
        )}
      </div>

      <EditableDataTable
        columns={columns}
        data={pitches}
        keyExtractor={(_, idx) => `pitch-${idx}`}
        enableEditing={canEdit}
      />
    </div>
  );
};

